import { useCopy } from '../content'
import type { ServiceKey } from '../content/types'
import { RELATED_SERVICES, SERVICE_MEDIA_ROLE, SERVICE_VIDEO } from '../data/services'
import { CtaBand } from '../components/layout/CtaBand'
import { Section } from '../components/layout/Section'
import { Faq } from '../components/home/Faq'
import { ReelVideo } from '../components/media/ReelVideo'
import { srcFor } from '../components/media/srcset'
import { IncludesList } from '../components/service/IncludesList'
import { RelatedServices } from '../components/service/RelatedServices'
import { ServiceHero } from '../components/service/ServiceHero'

/**
 * One template for the eight service pages (spec §4.3): photo hero with the
 * breadcrumb and the page's `<h1>` → what it includes → detail block → page
 * FAQ → three related services → CTA band to the contact page. (The owner
 * removed the «Come lavoriamo» steps band on 2026-09-29.)
 *
 * The detail block pairs the text with the service's reel (spec decision 4):
 * a fixed 3:2 box, the poster in the markup, the clip loaded on view. Its
 * surface band follows the paper includes band, so the tone change is the
 * seam.
 */
export default function ServicePage({ serviceKey }: { serviceKey: ServiceKey }) {
  const copy = useCopy()
  const page = copy.pages[serviceKey]
  return (
    <main id="main">
      <ServiceHero serviceKey={serviceKey} />
      <IncludesList title={page.includesTitle} items={page.includes} />
      <Section tone="surface">
        <h2 className="vc2-display text-3xl md:text-4xl">{page.detailTitle}</h2>
        <div className="mt-8 grid items-start gap-10 lg:grid-cols-2">
          <p className="text-lg text-vc2-ink-soft">{page.detailText}</p>
          <div className="aspect-[3/2] overflow-hidden rounded-2xl bg-vc2-surface">
            <ReelVideo src={SERVICE_VIDEO[serviceKey]} poster={srcFor(SERVICE_MEDIA_ROLE[serviceKey], 1600, 'jpg')} label={page.name} />
          </div>
        </div>
      </Section>
      <Faq items={page.faq} title={copy.faq.title} eyebrow={copy.faq.eyebrow} />
      <RelatedServices keys={RELATED_SERVICES[serviceKey]} title={page.relatedTitle} />
      <CtaBand title={page.ctaLabel} text={copy.contact.intro} ctaLabel={copy.hero.ctaPrimary} />
    </main>
  )
}
